'use client'

import { useEffect, useLayoutEffect, useState } from 'react'
import { Portal } from '@mantine/core'
import { IconX } from '@tabler/icons-react'
import { SearchInputField } from './SearchInputField'
import { SearchSuggestionPanel } from './SearchSuggestionPanel'
import type { ResolvedSearchPlacement, SearchTermSuggestion } from './universalSearchTypes'
import type { UseUniversalSearchReturn } from './useUniversalSearch'
import sharedClasses from './UniversalSearch.module.css'
import classes from './MobileSearch.module.css'

interface MobileSearchProps {
    /** Hint text shown when the search field is empty. */
    placeholder: string
    ariaLabel?: string
    /** Starter chips shown when the search field is empty. */
    termSuggestions: SearchTermSuggestion[]
    /** Shared search state and handlers from `useUniversalSearch`. */
    search: UseUniversalSearchReturn
    /** Enter submits the search; Escape closes the panel. */
    onKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => void
    /**
     * Closed-state layout. `dock` pins the field to the bottom of the screen.
     * `inline` keeps it in the page until the panel opens. `trigger` hides the
     * field until something else focuses its input.
     */
    resolvedPlacement: ResolvedSearchPlacement
}

/**
 * Where the visible screen currently is. Lets the open search panel sit in
 * the space above the keyboard instead of under it.
 */
interface VisualViewportBounds {
    /** How far the visible screen has shifted down. */
    top: number
    /** How much of the screen is still visible. */
    height: number
}

/** Ignore taps that belong to the same gesture that opened the panel. */
const PANEL_OPEN_GESTURE_GUARD_MS = 250

/**
 * Mobile search. The same text field stays on screen from closed to open.
 *
 * Closed, it either sits at the bottom of the screen (dock) or in the page
 * (inline). Open, suggestions fill the space above it, and it stays in the
 * part of the screen you can still see so the keyboard does not hide it.
 *
 * When docked or triggered, we draw the field and its background on top of the
 * whole page so they are not trapped behind a sticky header.
 */
export function MobileSearch({
    placeholder,
    ariaLabel,
    termSuggestions,
    search,
    onKeyDown,
    resolvedPlacement,
}: MobileSearchProps) {
    /** Visible-screen box while open, so the panel sits above the keyboard. */
    const [visualViewportBounds, setVisualViewportBounds] = useState<VisualViewportBounds | null>(null)
    /** False until the opening tap has ended, so it cannot select a suggestion. */
    const [isPanelInteractive, setIsPanelInteractive] = useState(false)

    useEffect(() => {
        if (!search.isOpen) {
            setIsPanelInteractive(false)
            return
        }

        const timer = window.setTimeout(() => {
            setIsPanelInteractive(true)
        }, PANEL_OPEN_GESTURE_GUARD_MS)

        return () => window.clearTimeout(timer)
    }, [search.isOpen])

    /**
     * Keep the open panel filling the visible screen as the keyboard and
     * address bar change size.
     */
    useLayoutEffect(() => {
        if (!search.isOpen) {
            setVisualViewportBounds(null)
            return
        }

        const viewport = window.visualViewport
        if (!viewport) {
            return
        }

        const syncViewportBounds = () => {
            setVisualViewportBounds({
                top: viewport.offsetTop,
                height: viewport.height,
            })
        }

        syncViewportBounds()
        viewport.addEventListener('resize', syncViewportBounds)
        viewport.addEventListener('scroll', syncViewportBounds)

        return () => {
            viewport.removeEventListener('resize', syncViewportBounds)
            viewport.removeEventListener('scroll', syncViewportBounds)
        }
    }, [search.isOpen])

    // Stays mounted so focus() inside the opening tap raises the iOS keyboard.
    const isTriggerClosed = !search.isOpen && resolvedPlacement === 'trigger'

    const shellClassName = [
        classes.shell,
        search.isOpen ? classes.shellOpen : '',
        !search.isOpen && resolvedPlacement === 'dock' ? classes.shellDocked : '',
        !search.isOpen && resolvedPlacement === 'inline' ? classes.shellInline : '',
        isTriggerClosed ? classes.shellTrigger : '',
    ]
        .filter(Boolean)
        .join(' ')

    const handleClose = () => {
        search.inputRef.current?.blur()
        search.close()
    }

    const handleShellBlur = (event: React.FocusEvent<HTMLDivElement>) => {
        const nextFocused = event.relatedTarget
        if (search.isOpen && nextFocused instanceof Node && !event.currentTarget.contains(nextFocused)) {
            search.close()
        }
    }

    const handlePanelClickCapture = (event: React.MouseEvent) => {
        if (!isPanelInteractive) {
            event.preventDefault()
            event.stopPropagation()
        }
    }

    const handleInputPointerDown = (event: React.PointerEvent<HTMLLabelElement>) => {
        if (search.isOpen) {
            return
        }

        // Keep the page from scrolling the closed field into view on tap.
        event.preventDefault()

        const input = search.inputRef.current
        if (!input) {
            return
        }

        // Opening runs from onFocus, but focus() does nothing when the field is
        // already focused (for example after Escape closed the panel without blur).
        if (document.activeElement === input) {
            search.handleFocus()
            return
        }

        input.focus({ preventScroll: true })
    }

    const viewportSizedStyle: React.CSSProperties | undefined = visualViewportBounds
        ? {
              top: visualViewportBounds.top,
              height: visualViewportBounds.height,
          }
        : undefined

    // visualViewport uses top/height; drop CSS `bottom: 0` so those measurements win.
    const shellStyle: React.CSSProperties | undefined =
        search.isOpen && viewportSizedStyle
            ? {
                  ...viewportSizedStyle,
                  bottom: 'auto',
              }
            : undefined

    // Beige layer behind the open panel, sized to the visible screen.
    const layoutCover = search.isOpen ? (
        <div
            className={classes.layoutCover}
            aria-hidden
            data-testid="mobile-search-layout-cover"
            style={viewportSizedStyle}
        />
    ) : null

    const searchShell = (
        <div
            className={shellClassName}
            style={shellStyle}
            onBlur={handleShellBlur}
            aria-hidden={isTriggerClosed || undefined}
            data-testid="mobile-search-shell"
        >
            {search.isOpen && (
                <div
                    className={[classes.panel, isPanelInteractive ? '' : classes.panelOpening]
                        .filter(Boolean)
                        .join(' ')}
                    onClickCapture={handlePanelClickCapture}
                >
                    <button
                        type="button"
                        className={classes.closeButton}
                        onClick={handleClose}
                        aria-label="Close search"
                    >
                        <IconX size={20} />
                    </button>

                    <div className={classes.panelContent}>
                        {!search.isSuggestionListHidden && (
                            <SearchSuggestionPanel
                                variant="mobile"
                                query={search.query}
                                termSuggestions={termSuggestions}
                                searchSuggestions={search.searchSuggestions}
                                isTypeaheadPending={search.isTypeaheadPending}
                                onTermSelect={search.selectTermSuggestion}
                                onSubmit={() => search.submit(undefined, 'search_query_click')}
                            />
                        )}
                    </div>
                </div>
            )}

            <div className={classes.inputArea}>
                <div className={classes.dockBackdrop} aria-hidden />
                <label className={classes.inputLabel} aria-label={placeholder} onPointerDown={handleInputPointerDown}>
                    <SearchInputField
                        variant={resolvedPlacement === 'inline' && !search.isOpen ? 'inlineClosed' : 'dock'}
                        value={search.query}
                        placeholder={placeholder}
                        ariaLabel={ariaLabel}
                        onChange={search.setQuery}
                        onFocus={search.handleFocus}
                        onKeyDown={onKeyDown}
                        inputRef={search.inputRef}
                        isLoading={search.isInputLoading}
                        tabIndex={isTriggerClosed ? -1 : undefined}
                    />
                </label>
            </div>
        </div>
    )

    // Inline keeps the closed field in the page and portals only the open cover.
    // Dock and trigger portal both, so a sticky or hidden parent cannot trap them.
    return (
        <div className={sharedClasses.root}>
            {resolvedPlacement === 'inline' ? (
                <>
                    {layoutCover && <Portal>{layoutCover}</Portal>}
                    {searchShell}
                </>
            ) : (
                <Portal>
                    <>
                        {layoutCover}
                        {searchShell}
                    </>
                </Portal>
            )}
        </div>
    )
}
